import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { SignupStateService } from '../../services/signup-state.service';
import { CompanyRegistrationService } from 'src/app/auth/services/company-registration.service';
import {
  KycApiService,
  PepChileCheckResult,
} from 'src/app/auth/services/kyc-api.service';
import { PEP_FUNCIONES } from '../../constants/pep-empresa.constants';
import { PASOS_EMPRESA } from '../../constants/signup.constants';

/**
 * Paso 10 (empresa): declaracion PEP del representante legal.
 *
 * Consulta la lista PEP de Chile, pide la declaracion y, al enviarla, persiste
 * todo el paso del representante legal: `data1` con el formulario del paso
 * anterior mas esta declaracion, y `data4` con las rutas de las imagenes de
 * identidad que dejo la verificacion facial.
 * Se escribe aca y no antes para que `data1` se guarde una sola vez y completo.
 */
@Component({
  selector: 'app-representante-pep-step',
  templateUrl: './representante-pep-step.component.html',
  styleUrls: ['./representante-pep-step.component.sass'],
})
export class RepresentantePepStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  form!: FormGroup;
  pepFunciones = PEP_FUNCIONES;

  loading = false;

  // Consulta a la lista PEP de Chile
  pepCheckLoading = false;
  pepCheckResult: PepChileCheckResult | null = null;

  private userId: string | number | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    public signupState: SignupStateService,
    private companyService: CompanyRegistrationService,
    private kycService: KycApiService
  ) {}

  ngOnInit(): void {
    // Prerrequisito: el formulario del representante legal
    if (!this.signupState.representanteLegal) {
      this.signupState.goToStep(PASOS_EMPRESA.REPRESENTANTE);
      return;
    }

    const authSub = this.store.select('auth').subscribe(({ user }: any) => {
      this.userId = user?.usuarioId ?? null;
    });
    this.subscriptions.push(authSub);

    this.initForm();
    this.checkPepChile();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  // ==================== FORMULARIO ====================

  private initForm(): void {
    const saved = this.signupState.representanteLegal;

    this.form = this.fb.group({
      es_pep: [saved?.es_pep ?? null, Validators.required],
      pep_funcion: [saved?.pep_funcion || ''],
      pep_institucion: [saved?.pep_institucion || ''],
      pep_fecha_inicio: [saved?.pep_fecha_inicio || ''],
      pep_fecha_termino: [saved?.pep_fecha_termino || ''],
    });

    if (saved?.es_pep === true) {
      this.applyPepValidators(true);
    }
  }

  /** Los campos PEP solo son obligatorios cuando el representante declara que si. */
  private applyPepValidators(isPep: boolean): void {
    const pepControls = ['pep_funcion', 'pep_institucion', 'pep_fecha_inicio'];

    pepControls.forEach((name) => {
      const control = this.form.get(name);
      if (!control) return;

      if (isPep) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
        control.setValue('');
      }
      control.updateValueAndValidity();
    });

    if (!isPep) {
      this.form.get('pep_fecha_termino')?.setValue('');
    }
  }

  setPep(isPep: boolean): void {
    this.form.get('es_pep')?.setValue(isPep);
    this.form.get('es_pep')?.markAsTouched();
    this.applyPepValidators(isPep);
  }

  get isPep(): boolean {
    return this.form?.get('es_pep')?.value === true;
  }

  // ==================== CONSULTA PEP CHILE ====================

  private async checkPepChile(): Promise<void> {
    const ocr = this.signupState.currentState.ocrData;
    const dni = ocr?.documentNumberClean || ocr?.documentNumber;
    if (!dni) return;

    this.pepCheckLoading = true;

    try {
      const apellidos = (ocr?.lastName || '').trim().split(/\s+/);

      this.pepCheckResult = await this.kycService.checkPepChile(
        {
          dni,
          personType: 'natural',
          name: ocr?.firstName || undefined,
          fatherName: apellidos[0] || undefined,
          motherName: apellidos.length > 1 ? apellidos.slice(1).join(' ') : undefined,
          nationality: ocr?.nationality || undefined,
        },
        // duranteRegistro: un 401 aca expulsaria al usuario al login a mitad del alta
        true
      );

      // Coincidencia en lista: se premarca la declaracion, el usuario completa el detalle
      if (this.pepCheckResult?.isPep) {
        this.setPep(true);
      }
    } catch (error: any) {
      // Una falla de la consulta no bloquea: queda la declaracion del usuario
      this.pepCheckResult = null;
    } finally {
      this.pepCheckLoading = false;
    }
  }

  get pepDetectado(): boolean {
    return !!this.pepCheckResult?.isPep;
  }

  get pepListasCoincidentes(): string[] {
    return (this.pepCheckResult?.listsChecked || [])
      .filter((l) => l.coincidence)
      .map((l) => l.listName);
  }

  // ==================== ENVIO ====================

  async onSubmit(): Promise<void> {
    if (this.loading) return;

    if (this.form.invalid) {
      this.markFormAsTouched();
      this.onError.emit('Completa todos los campos requeridos.');
      return;
    }

    if (!this.userId) {
      this.onError.emit(
        'No pudimos identificar la empresa. Recarga la pagina e intenta nuevamente.'
      );
      return;
    }

    this.loading = true;

    try {
      const pepValues = this.form.getRawValue();
      const datosDelRepresentante = this.signupState.representanteLegal!;

      const representante = {
        ...datosDelRepresentante,
        ...pepValues,
        pep_deteccion_automatica: this.pepDetectado,
      };

      // Guardar en el estado antes de la red, para no perder lo escrito
      this.signupState.setRepresentanteLegal(representante);

      // data1: datos del representante legal, incluida la declaracion PEP
      await this.companyService.addDataToRegister(
        1,
        { ...representante, _format: 'new_ocr' },
        String(this.userId)
      );

      // data6: la declaracion PEP duplicada, porque es de donde la lee la
      // consolidacion (ver saveDeclaracionPep)
      await this.saveDeclaracionPep(pepValues);

      // data4: imagenes y resultado de la verificacion de identidad
      await this.saveIdentityData();

      this.onSuccess.emit('Datos del representante legal guardados');
      await this.signupState.nextStep();
    } catch (error: any) {
      this.onError.emit(
        error?.message || 'Error al guardar los datos. Intenta nuevamente.'
      );
    } finally {
      this.loading = false;
    }
  }

  /**
   * Guarda la declaracion PEP tambien en `data6`.
   *
   * No es redundancia: `consolidateUserAndCompany` lee `es_pep` **de `data6`**,
   * no de `data1`. Sin esta copia toda empresa se consolidaria como no expuesta
   * politicamente, incluida la que declaro que si.
   *
   * El monolito hacia exactamente esto al guardar su paso 6 (`uploadAllFiles`:
   * "el backend lee es_pep desde data6"), con los mismos cinco campos y los
   * mismos nombres. Se replica esa forma tal cual para que una empresa que
   * empiece aca y termine en el monolito —o al reves— tenga el dato en el
   * lugar donde cada lado lo busca.
   *
   * Va en un `step/6` propio y no dentro de `data1` porque `data6` es el slot
   * de la documentacion legal: cuando esas pantallas se completen (hoy solo
   * existen en el monolito) van a escribir sus archivos en el mismo slot, y el
   * PEP tiene que estar ahi de antes.
   */
  private async saveDeclaracionPep(pepValues: any): Promise<void> {
    await this.companyService.addDataToRegister(
      6,
      {
        es_pep: pepValues.es_pep ?? '',
        pep_funcion: pepValues.pep_funcion || '',
        pep_institucion: pepValues.pep_institucion || '',
        pep_fecha_inicio: pepValues.pep_fecha_inicio || '',
        pep_fecha_termino: pepValues.pep_fecha_termino || '',
      },
      String(this.userId)
    );
  }

  /**
   * Guarda `data4`: las rutas de las imagenes de identidad, el OCR, el
   * resultado facial y el analisis del e-RUT.
   *
   * Las imagenes ya estan en S3 desde el paso 8, asi que aca solo se
   * referencian. Antes se subian en este mismo metodo, y eso ataba `data4` a
   * que las base64 siguieran en memoria: un refresh entre medio dejaba el
   * registro con datos del representante y sin bloque de identidad.
   */
  private async saveIdentityData(): Promise<void> {
    const state = this.signupState.currentState;
    const s3Keys = state.s3Keys;

    // Sin rutas no se toca data4: reescribirlo con vacios borraria lo ya
    // guardado. Pasa con registros empezados antes de que la subida se moviera
    // al paso 8; esos vuelven al paso del OCR al restaurar y las consiguen ahi.
    if (!s3Keys?.documentFront || !s3Keys?.selfie) {
      return;
    }

    const keys: Record<string, string> = {
      selfie: s3Keys.selfie,
      documentFront: s3Keys.documentFront,
      documentBack: s3Keys.documentBack || '',
    };

    await this.companyService.addDataToRegister(
      4,
      {
        s3Keys: keys,
        ocr: {
          // Los campos CRUDOS del OCR: la consolidacion exige `nombres`,
          // `apellidos`, `runSinFormato` y `nacionalidad`, no los nombres
          // mapeados que usa el front. Ver `ocrCampos` en signup.interfaces.ts.
          campos: state.ocrCampos || {},
          fotoPersona: state.ocrData?.fotoPersona || '',
        },
        facialVerification: {
          isMatch: state.faceVerified,
          similarity: (state.faceVerificationResult?.similarity || 0) / 100,
          needsManualReview: !!state.faceVerificationResult?.needsManualReview,
        },
        erut: this.signupState.erutAnalysis,
      },
      String(this.userId)
    );
  }

  private markFormAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && control.touched;
  }

  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }
}
